import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const RiskHeatmapVisualization = ({ departments }) => {
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  const getRiskColor = (level) => {
    if (level <= 2) return 'bg-success/20 border-success/40 text-success';
    if (level <= 4) return 'bg-warning/20 border-warning/40 text-warning';
    if (level <= 6) return 'bg-error/20 border-error/40 text-error';
    return 'bg-destructive/20 border-destructive/40 text-destructive';
  };

  const getRiskLabel = (level) => {
    if (level <= 2) return 'Low Risk';
    if (level <= 4) return 'Medium Risk';
    if (level <= 6) return 'High Risk';
    return 'Critical';
  };

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 shadow-elevation-2 border border-border">
      <div className="flex items-start justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg lg:text-xl font-semibold text-foreground mb-1 md:mb-2">
            Risk Heatmap Visualization
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Department-level intervention priorities with drill-down
          </p>
        </div>
        <div className="p-2 md:p-3 rounded-lg bg-error/10">
          <Icon name="AlertTriangle" size={20} className="text-error" />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mb-4 md:mb-6">
        {departments?.map((dept) => (
          <div
            key={dept?.id}
            className={`border-2 rounded-lg p-4 cursor-pointer transition-smooth hover:shadow-elevation-2 ${getRiskColor(dept?.riskLevel)}`}
            onClick={() => setSelectedDepartment(dept)}
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="text-sm md:text-base font-semibold mb-1">{dept?.name}</h4>
                <p className="text-xs opacity-80">{dept?.employees} employees</p>
              </div>
              <div className="text-right">
                <p className="text-lg md:text-xl font-bold">{dept?.riskLevel}/10</p>
                <p className="text-xs opacity-80">{getRiskLabel(dept?.riskLevel)}</p>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs">
                <span className="opacity-80">High Stress</span>
                <span className="font-medium">{dept?.highStress}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="opacity-80">Debt Concerns</span>
                <span className="font-medium">{dept?.debtConcerns}</span>
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="opacity-80">Low Savings</span>
                <span className="font-medium">{dept?.lowSavings}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between p-3 md:p-4 bg-muted rounded-lg">
        <div className="flex items-center gap-2">
          <Icon name="Info" size={16} className="text-primary" />
          <p className="text-xs md:text-sm text-muted-foreground">
            Click on any department for detailed risk analysis
          </p>
        </div>
        <Button variant="outline" size="sm" iconName="Download">
          Export Report
        </Button>
      </div>
      {selectedDepartment && (
        <div className="fixed inset-0 z-[1300] flex items-center justify-center p-4 bg-background/80">
          <div className="bg-card rounded-xl shadow-elevation-5 p-6 md:p-8 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className="text-lg md:text-xl font-semibold text-foreground">
                  {selectedDepartment?.name} - Detailed Analysis
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Risk Level: {selectedDepartment?.riskLevel}/10 - {getRiskLabel(selectedDepartment?.riskLevel)}
                </p>
              </div>
              <button
                onClick={() => setSelectedDepartment(null)}
                className="p-1 rounded-lg hover:bg-muted transition-smooth"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <div className="space-y-4 md:space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-2">Total Employees</p>
                  <p className="text-2xl font-bold text-foreground">{selectedDepartment?.employees}</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-2">At-Risk Count</p>
                  <p className="text-2xl font-bold text-error">{selectedDepartment?.highStress + selectedDepartment?.debtConcerns}</p>
                </div>
                <div className="bg-muted rounded-lg p-4">
                  <p className="text-xs text-muted-foreground mb-2">Avg. Wellness Score</p>
                  <p className="text-2xl font-bold text-foreground">{selectedDepartment?.avgScore}</p>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">Risk Factors</h4>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="AlertCircle" size={16} className="text-error" />
                      <span className="text-sm text-foreground">High Financial Stress</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">{selectedDepartment?.highStress} employees</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="CreditCard" size={16} className="text-warning" />
                      <span className="text-sm text-foreground">Debt Concerns</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">{selectedDepartment?.debtConcerns} employees</span>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
                    <div className="flex items-center gap-2">
                      <Icon name="PiggyBank" size={16} className="text-warning" />
                      <span className="text-sm text-foreground">Low Savings Rate</span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">{selectedDepartment?.lowSavings} employees</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="text-sm font-semibold text-foreground mb-3">Recommended Actions</h4>
                <div className="space-y-2">
                  <div className="flex items-start gap-2 p-3 bg-primary/10 rounded-lg">
                    <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
                    <p className="text-xs md:text-sm text-foreground">
                      Assign debt management education module to high-risk employees
                    </p>
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-primary/10 rounded-lg">
                    <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
                    <p className="text-xs md:text-sm text-foreground">
                      Schedule one-on-one financial wellness consultations
                    </p>
                  </div>
                  <div className="flex items-start gap-2 p-3 bg-primary/10 rounded-lg">
                    <Icon name="CheckCircle" size={16} className="text-primary mt-0.5" />
                    <p className="text-xs md:text-sm text-foreground">
                      Implement emergency savings program with employer matching
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setSelectedDepartment(null)} fullWidth>
                  Close
                </Button>
                <Button variant="default" iconName="Send" fullWidth>
                  Send Intervention Plan
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RiskHeatmapVisualization;